import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { promptFieldService } from '../../../services/promptFieldService';

const PromptFieldManager = ({ useCaseId, aiToolId, useCaseName, aiToolName, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [managingOptionsFor, setManagingOptionsFor] = useState(null);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [optionFormData, setOptionFormData] = useState({
    optionValue: '',
    optionLabel: '',
    displayOrder: 0
  });
  const [editingOption, setEditingOption] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [formData, setFormData] = useState({
    fieldName: '',
    fieldLabel: '',
    fieldType: 'text',
    placeholder: '',
    description: '',
    isRequired: false,
    displayOrder: 0
  });

  const loadFields = async () => {
    try {
      setLoading(true);
      const data = await promptFieldService?.getPromptFieldsByUseCaseAndTool(useCaseId, aiToolId);
      setFields(data);
    } catch (err) {
      console.error('Error loading fields:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFieldOptions = async (fieldId) => {
    try {
      setLoading(true);
      const options = await promptFieldService?.getPromptFieldOptions(fieldId);
      setFieldOptions(options);
    } catch (err) {
      console.error('Error loading field options:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    loadFields();
  };

  const handleClose = () => {
    setIsOpen(false);
    setEditingField(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      fieldName: '',
      fieldLabel: '',
      fieldType: 'text',
      placeholder: '',
      description: '',
      isRequired: false,
      displayOrder: 0
    });
  };

  const handleEdit = (field) => {
    setEditingField(field?.id);
    setFormData({
      fieldName: field?.fieldName,
      fieldLabel: field?.fieldLabel,
      fieldType: field?.fieldType,
      placeholder: field?.placeholder,
      description: field?.description,
      isRequired: field?.isRequired,
      displayOrder: field?.displayOrder
    });
  };

  const handleSave = async () => {
    try {
      if (editingField) {
        await promptFieldService?.updatePromptField(editingField, formData);
      } else {
        await promptFieldService?.createPromptField({
          ...formData,
          useCaseId,
          aiToolId
        });
      }
      await loadFields();
      resetForm();
      setEditingField(null);
      onRefresh?.();
    } catch (err) {
      console.error('Error saving field:', err);
    }
  };

  const handleDelete = async (fieldId) => {
    if (!confirm('Are you sure you want to delete this field?')) return;
    
    try {
      await promptFieldService?.deletePromptField(fieldId);
      await loadFields();
      onRefresh?.();
    } catch (err) {
      console.error('Error deleting field:', err);
    }
  };

  const handleManageOptions = (field) => {
    setManagingOptionsFor(field);
    loadFieldOptions(field?.id);
  };

  const handleCloseOptionsManager = () => {
    setManagingOptionsFor(null);
    setFieldOptions([]);
    setOptionFormData({ optionValue: '', optionLabel: '', displayOrder: 0 });
    setEditingOption(null);
  };

  const handleSaveOption = async () => {
    try {
      if (editingOption) {
        await promptFieldService?.updatePromptFieldOption(editingOption, optionFormData);
      } else {
        await promptFieldService?.createPromptFieldOption({
          ...optionFormData,
          promptFieldId: managingOptionsFor?.id
        });
      }
      await loadFieldOptions(managingOptionsFor?.id);
      setOptionFormData({ optionValue: '', optionLabel: '', displayOrder: 0 });
      setEditingOption(null);
    } catch (err) {
      console.error('Error saving option:', err);
    }
  };

  const handleEditOption = (option) => {
    setEditingOption(option?.id);
    setOptionFormData({
      optionValue: option?.optionValue,
      optionLabel: option?.optionLabel,
      displayOrder: option?.displayOrder
    });
  };

  const handleDeleteOption = async (optionId) => {
    if (!window?.confirm('Are you sure you want to delete this option?')) return;
    
    try {
      await promptFieldService?.deletePromptFieldOption(optionId);
      await loadFieldOptions(managingOptionsFor?.id);
    } catch (err) {
      console.error('Error deleting option:', err);
    }
  };

  const handleImport = async () => {
    try {
      setImportError(null);
      setImportSuccess(null);
      setLoading(true);

      // Parse JSON
      const parsedData = JSON.parse(importJson);
      
      // Validate structure
      if (!parsedData?.useCase || !parsedData?.aiTools || !Array.isArray(parsedData?.aiTools)) {
        throw new Error('Invalid JSON structure. Expected format: { useCase: "...", aiTools: [...] }');
      }

      // Call import service
      const result = await promptFieldService?.importPromptFieldsForUseCaseAndTool(parsedData);
      
      setImportSuccess(result?.message);
      setImportJson('');
      
      // Reload fields after successful import
      await loadFields();
      
      // Close import modal after 2 seconds
      setTimeout(() => {
        setShowImportModal(false);
        setImportSuccess(null);
      }, 2000);

      // Refresh parent if needed
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Import error:', err);
      setImportError(err?.message || 'Failed to import data');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportJson('');
    setImportError(null);
    setImportSuccess(null);
  };

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'select', label: 'Select' },
    { value: 'checkbox', label: 'Checkbox' }
  ];

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpen}
        iconName="Settings"
        iconPosition="left"
      >
        Manage Prompt Fields
      </Button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-foreground">Manage Prompt Fields</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {useCaseName} → {aiToolName}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportModal(true)}
                iconName="Upload"
                iconPosition="left"
              >
                Import JSON
              </Button>
              <button
                onClick={handleClose}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : managingOptionsFor ? (
            // Options Manager View
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-foreground">Manage Dropdown Options</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Field: {managingOptionsFor?.fieldLabel}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCloseOptionsManager}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Back to Fields
                </Button>
              </div>

              {/* Option Form */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                <h5 className="font-medium text-foreground">
                  {editingOption ? 'Edit Option' : 'Add New Option'}
                </h5>
                
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Option Value"
                    placeholder="e.g., realistic"
                    value={optionFormData?.optionValue}
                    onChange={(e) => setOptionFormData({ ...optionFormData, optionValue: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Option Label"
                    placeholder="e.g., Realistic"
                    value={optionFormData?.optionLabel}
                    onChange={(e) => setOptionFormData({ ...optionFormData, optionLabel: e?.target?.value })}
                    required
                  />
                </div>

                <Input
                  label="Display Order"
                  type="number"
                  value={optionFormData?.displayOrder}
                  onChange={(e) => setOptionFormData({ ...optionFormData, displayOrder: parseInt(e?.target?.value) || 0 })}
                />

                <div className="flex gap-2">
                  <Button onClick={handleSaveOption} iconName="Save" iconPosition="left">
                    {editingOption ? 'Update Option' : 'Add Option'}
                  </Button>
                  {editingOption && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingOption(null);
                        setOptionFormData({ optionValue: '', optionLabel: '', displayOrder: 0 });
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>

              {/* Options List */}
              <div>
                <h5 className="font-medium text-foreground mb-3">Existing Options ({fieldOptions?.length})</h5>
                {fieldOptions?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No options configured yet. Add your first option above.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {fieldOptions?.map((option) => (
                      <div
                        key={option?.id}
                        className="flex items-center justify-between p-3 bg-background border border-border rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">{option?.optionLabel}</span>
                            <span className="text-xs text-muted-foreground">({option?.optionValue})</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Order: {option?.displayOrder}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditOption(option)}
                            iconName="Edit"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteOption(option?.id)}
                            iconName="Trash2"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Fields List View
            <div className="space-y-6">
              {/* Form */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                <h4 className="font-medium text-foreground">
                  {editingField ? 'Edit Field' : 'Add New Field'}
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Field Name"
                    placeholder="e.g., subject_description"
                    value={formData?.fieldName}
                    onChange={(e) => setFormData({ ...formData, fieldName: e?.target?.value })}
                    required
                  />
                  <Input
                    label="Field Label"
                    placeholder="e.g., Subject Description"
                    value={formData?.fieldLabel}
                    onChange={(e) => setFormData({ ...formData, fieldLabel: e?.target?.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label="Field Type"
                    options={fieldTypes}
                    value={formData?.fieldType}
                    onChange={(value) => setFormData({ ...formData, fieldType: value })}
                  />
                  <Input
                    label="Display Order"
                    type="number"
                    value={formData?.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e?.target?.value) || 0 })}
                  />
                </div>

                <Input
                  label="Placeholder"
                  placeholder="e.g., Describe the main subject..."
                  value={formData?.placeholder}
                  onChange={(e) => setFormData({ ...formData, placeholder: e?.target?.value })}
                />

                <Input
                  label="Description"
                  placeholder="Helper text for users"
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                />

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData?.isRequired}
                    onChange={(e) => setFormData({ ...formData, isRequired: e?.target?.checked })}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Required field</span>
                </label>

                <div className="flex gap-2">
                  <Button onClick={handleSave} iconName="Save" iconPosition="left">
                    {editingField ? 'Update Field' : 'Add Field'}
                  </Button>
                  {editingField && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingField(null);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>

              {/* Fields List */}
              <div>
                <h4 className="font-medium text-foreground mb-3">Existing Fields ({fields?.length})</h4>
                {fields?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No fields configured yet. Add your first field above.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {fields?.map((field) => (
                      <div
                        key={field?.id}
                        className="flex items-center justify-between p-3 bg-background border border-border rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">{field?.fieldLabel}</span>
                            {field?.isRequired && (
                              <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">Required</span>
                            )}
                            <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                              {field?.fieldType}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {field?.fieldName} • Order: {field?.displayOrder}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleManageOptions(field)}
                            iconName="List"
                            title="Manage dropdown options"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(field)}
                            iconName="Edit"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(field?.id)}
                            iconName="Trash2"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border">
          <Button variant="outline" onClick={handleClose} className="w-full">
            Close
          </Button>
        </div>
      </div>

      {/* Import JSON Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-foreground">Import JSON Data</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Paste your JSON data to override existing fields for {useCaseName} - {aiToolName}
                  </p>
                </div>
                <button
                  onClick={handleCloseImportModal}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* JSON Input */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    JSON Data
                  </label>
                  <textarea
                    value={importJson}
                    onChange={(e) => setImportJson(e?.target?.value)}
                    placeholder='Paste your JSON here...\n\nExample:\n{\n  "useCase": "Image",\n  "aiTools": [...]\n}'
                    className="w-full h-64 px-3 py-2 border border-border rounded-lg bg-background text-foreground font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                {/* Error Message */}
                {importError && (
                  <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-destructive">Import Failed</p>
                        <p className="text-sm text-destructive/80 mt-1">{importError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {importSuccess && (
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Icon name="CheckCircle" size={20} className="text-green-500 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-500">Import Successful</p>
                        <p className="text-sm text-green-500/80 mt-1">{importSuccess}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Format Info */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium text-foreground mb-2">Expected JSON Format:</p>
                  <pre className="text-xs text-muted-foreground font-mono overflow-x-auto">
{`{
  "useCase": "Image",
  "aiTools": [
    {
      "toolName": "ChatGPT",
      "promptFields": [
        {
          "fieldName": "Subject Description",
          "fieldLabel": "Subject Description",
          "fieldType": "TextArea",
          "order": 1,
          "placeholder": "Describe...",
          "description": "Main subject",
          "required": true,
          "dropdownOptions": []
        }
      ]
    }
  ]
}`}
                  </pre>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-border">
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={handleCloseImportModal}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={loading || !importJson?.trim()}
                  iconName="Upload"
                  iconPosition="left"
                >
                  {loading ? 'Importing...' : 'Import Data'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptFieldManager;